import React, { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IFundSection } from "./SecondaryNav.types";
import { useInViewObserver } from "./useInViewObserver";
import "./secondaryNav.scss";
import { jumpToSection } from "./jumpToSection";
import { getCleanPageURL } from "./setSecondaryNavState";
import HTMLReactParser from "html-react-parser";
import { createPortal } from 'react-dom';

export interface ISecondaryNav extends ComponentInterface { }

//Handle bots
const isCrawler = /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent);
//Whether or not to render <a> tags around secondary nav items. Might be needed for Crawlers.
const addLinksToSecondaryNav = false;

export interface HeaderHeightEvent extends Event {
    detail?: {
        headerHeight: number | undefined,
        cumilativeAlertBarsHeight: number | undefined
    }
}

const headerHeightEventHandler = (e: HeaderHeightEvent) => {
    if (!e.detail) return;
    let secondayNavOffsetTop = (e.detail.headerHeight ?? 0) + (e.detail.cumilativeAlertBarsHeight ?? 0);
    return secondayNavOffsetTop;
}

//For when the fund launch promo-banner is there on the page.
const stickySecondaryNavScrollHandler = (e: Event, setFixedClass: React.Dispatch<React.SetStateAction<string>>) => {
    const fundPageBody = document.querySelector(".fund-details-body-wrp");
    const stickeyHeaderHeight = document.querySelector("#sticky-site-header")?.getBoundingClientRect().height ?? 0;

    //see if fundpage body is under the sticky header.
    if (fundPageBody && fundPageBody.getBoundingClientRect().top < stickeyHeaderHeight + 75) {
        setFixedClass("");
    } else {
        setFixedClass("unfixed");
    }
}

export default function SecondaryNav(props: ISecondaryNav) {
    const element = props.rootElement;
    element.classList.remove("d-none");
    const [fixedClass, setFixedClass] = useState("");
    const [FundSections, setFundSections] = useState(getFundSections());
    const cleanURL = getCleanPageURL(FundSections);
    const [topOffset, setTopOffset] = useState(178);

    //Jump to the relavent section if needed first, then observe.
    useSectionJumpOnURL(FundSections);
    useInViewObserver(FundSections, setFundSections);

    useEffect(() => {
        document.addEventListener("headerHeightEvent", (e) => {
            let offSet = headerHeightEventHandler(e) ?? topOffset;
            offSet += 50; // sticky-header fixed height of 50px is added
            setTopOffset(offSet);
        });
    }, []);

    useEffect(() => {
        if (!document.querySelector(".fund-banner-wrapper .desktop-content")) return;

        setFixedClass("unfixed");
        window.addEventListener("scroll", (e) => stickySecondaryNavScrollHandler(e, setFixedClass));

        return () => {

            window.removeEventListener("scroll", (e) => stickySecondaryNavScrollHandler(e, setFixedClass));
        }
    }, [])

    return (
        <>
            <nav className={`sticky-left-sidebar ${fixedClass} nav-trans`} aria-label="Jump to" style={{ top: topOffset }}>
                <ul className="sidebar-nav">
                    {FundSections &&
                        FundSections.map((FundSection) => (
                            <SecondaryNavItem
                                key={FundSection.id as React.Key}
                                FundSection={FundSection}
                                displayClass="sidebar-nav__item"
                                cleanURL={cleanURL}
                            ></SecondaryNavItem>
                        ))}
                </ul>
            </nav>
            <StickyHeader FundSections={FundSections} />
        </>
    );
}

function StickyHeader({ FundSections }: { FundSections: IFundSection[] }) {
    const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
    const ticker = useMemo(() => document.querySelector(".fund-title-wrapper div.ticker")?.textContent?.trim() ?? "", []);
    const fundName = useMemo(() => document.getElementsByTagName("ve-fundname")[0]?.textContent?.trim() ?? "", []);
    const activeSectionLabel = FundSections.find((x) => x.activeClass == "active")?.name ?? "";
    const [secondaryNavTop, setsecondaryNavTop] = useState(0);
    const cleanURL = getCleanPageURL(FundSections);
    const isMutualFund = useMemo(() => document.getElementsByClassName("mutual-fund-page")[0] ? true : false, []);
    const [ShowStaticMobileDropdown] = useToggleStaticMobileDropdown();

    /**
     * The alertbar code will dispatch an event to document whenever the alertbars are added or removed from the page.
     * We need to listen to these events and adjust the offset of our sticky nav accordingly.
     * Refer generic-promo-banner.js for details of dispatcher.
     */


    useEffect(() => {
        const defaultHeaderHeight = document.querySelector(".root .sticky-wrapper")?.getBoundingClientRect().height ?? 0
        setsecondaryNavTop(defaultHeaderHeight);
        document.addEventListener("headerHeightEvent", (e) => {
            let offSet = headerHeightEventHandler(e) ?? secondaryNavTop;
            setsecondaryNavTop(offSet);
        });
    }, []);
    
    return (
        <div className="sticky-header" style={{ top: secondaryNavTop }}>
            <div className="container">
                <div className="inner-container">
                    <FundLabelContent ticker={ticker} fundName={fundName} />
                    <Breadcrumbs />
                    <NavigationLinks />
                    <div className="md-secondary-nav dropdown align-items-center">
                        <span className="text-util-md pr-2">{activeSectionLabel}</span>
                        <DesktopDropdownButton buttonClass={null} buttonText={null} isMobile={false} />
                        <nav className="dropdown-menu dropdown-menu-right" aria-label="Jump to">
                            {FundSections &&
                                FundSections.map((FundSection) => (
                                    <SecondaryNavItem
                                        key={FundSection.id as React.Key}
                                        FundSection={FundSection}
                                        displayClass="dropdown-item text-util-md"
                                        cleanURL={cleanURL}
                                    ></SecondaryNavItem>
                                ))}
                        </nav>
                    </div>
                </div>
                <div className="inner-container-mobile">
                    <FundLabelContent ticker={ticker} fundName={fundName} isMutualFund={isMutualFund} />
                    <Breadcrumbs />
                    <MobileButtonAndDropdown FundSections={FundSections} cleanURL={cleanURL} activeSectionLabel={activeSectionLabel as string} />
                    {
                        //add the desktopdropdownbutton also in the "fund-dropdown-portal" div, using a React portal
                        ShowStaticMobileDropdown && document.getElementById("fund-dropdown-portal") && createPortal(
                            <MobileButtonAndDropdown FundSections={FundSections} cleanURL={cleanURL} activeSectionLabel={activeSectionLabel as string} />,
                            document.getElementById("fund-dropdown-portal") as Element
                        )
                    }
                </div>
            </div>
        </div>
    );
}

/**
 * Hides and shows the static dropdown in mobile, based on the presence of fund-announcements or fund-launch-banner.
 * @returns [ShowStaticMobileDropdown] - Whether or not to show the static dropdown in mobile.
 */
function useToggleStaticMobileDropdown() {

    const [ShowStaticMobileDropdown, setShowStaticMobileDropdown] = useState(true);

    useEffect(() => {
        const fundAnnouncements = document.querySelector(".fund-announcements #fundAnnouncements-desktop");
        const fundLaunchBanner = document.querySelector(".fund-banner-wrapper .mobile-content");

        if (fundAnnouncements || fundLaunchBanner) {
            setShowStaticMobileDropdown(false);
            document.getElementById("fund-dropdown-portal")?.classList.add("d-none");
            return;
        }
        setShowStaticMobileDropdown(true);
        document.getElementById("fund-dropdown-portal")?.classList.remove("d-none");
    }, [])

    return [ShowStaticMobileDropdown];
}

//declare jQuery bootstrap;
declare global {
    interface Window { $: Function | any }
}

function MobileButtonAndDropdown({ FundSections, cleanURL, activeSectionLabel }: { FundSections: IFundSection[], cleanURL: string, activeSectionLabel: string }) {
    return (<div className="mobile-secondary-nav dropdown align-items-center">
        <DesktopDropdownButton buttonClass={null} buttonText={activeSectionLabel as string} isMobile={true} />
        <nav className="dropdown-menu dropdown-menu-right" aria-label="Jump to">
            {FundSections &&
                FundSections.map((FundSection) => (
                    <SecondaryNavItem
                        key={FundSection.id as React.Key}
                        FundSection={FundSection}
                        displayClass="dropdown-item text-util-md"
                        cleanURL={cleanURL}
                    ></SecondaryNavItem>
                ))}
        </nav>
    </div>)
}

function NavigationLinks() {
    const navLinks = useMemo(() => getNavigationLinks(), [])
    const navRefs = useRef<HTMLAnchorElement | null>(null);

    useEffect(function () {
        if (navRefs.current != null) {
            navRefs.current.addEventListener("click", function (e) {
                e.preventDefault();
                window.$("#id-how-to-buy-etf-modal")?.modal();
                window.$("#id-how-to-buy-mf-modal")?.modal("show");
            })
        }
    }, [])

    return <div className="sticky-links">
        {navLinks.map((link, index) => {
            return (<a href={link.url as string} data-cssclass={link.cssClass} data-ve-gtm={link.gtmAttribute} key={index} target={link.target} className="sticky-link px-3 text-util-md" ref={ref => link.cssClass.includes("id-how-to-buy-etf-modal") || link.cssClass.includes("id-how-to-buy-mf-modal") ? navRefs.current = ref : false}>
                {link.title}
            </a>)
        })}
    </div>;
}

type INavigationLinks = {
    title: string,
    url: string,
    cssClass: string,
    gtmAttribute: string,
    target: string
}

function getNavigationLinks(): INavigationLinks[] {
    const navLinks = document.getElementsByTagName("ve-secondarynavlinks");
    const FirstLink = navLinks[0];
    if (FirstLink == null) return [];
    const linkItems = Array.from(FirstLink.getElementsByTagName("ve-linkitem")) as HTMLElement[];
    const linkList = linkItems.map(link => {
        return {
            title: link.dataset.title ?? "",
            url: link.dataset.url ?? "",
            cssClass: link.dataset.cssclass ?? "",
            gtmAttribute: link.dataset.gtmtag ?? "",
            target: link.dataset.target ?? "_self"
        };
    });
    return linkList;
}

function FundLabelContent({ ticker, fundName, isMutualFund }: { ticker: string; fundName: string, isMutualFund?: boolean }) {
    var dropdown = document.getElementsByClassName("mf-fund-selector")[0];
    useEffect(()=>{
        if(dropdown){
            var wrapperDiv = document.getElementsByClassName("mf-dropdown-div")[0];
            wrapperDiv.innerHTML = dropdown.outerHTML
        }
    },[])
    if(isMutualFund){
        return(
            <div className="fund-label d-flex align-items-center my-2">
                <div className="fund-name-label px-2">{fundName}</div>
                <div className="ticker-label px-2 font-weight-bold">{ticker}</div>
                <div className="mf-dropdown-div"></div>
            </div>
        )
    }
    else return (
        <div className="fund-label d-flex">
            <div className="ticker-label pr-2 font-weight-bold">{ticker}</div>
            <div className="fund-name-label px-2">{fundName} </div>
        </div>
    );
}

export function Breadcrumbs() {
    const pageBreadcrumb = document.querySelectorAll(".fund-breadcrumb-wrp");
    if (pageBreadcrumb.length === 0) return <></>;
    const breadcrumbMarkup = pageBreadcrumb[0].innerHTML;
    return <div className="fund-breadcrumb-wrp laptop-only">{HTMLReactParser(breadcrumbMarkup)}</div>
}

export function getFundSections(): IFundSection[] {
    const FundPageSectionNodes = document.querySelectorAll("section.fund-page-section");
    return Array.prototype.map.call(FundPageSectionNodes, function (sectionNode: Element): IFundSection {
        return {
            id: sectionNode.id,
            name: sectionNode.querySelector("h2.fund-page-section-header")?.getAttribute("data-section-name") ?? null,
            activeClass: "",
        };
    }) as IFundSection[];
}

const clickListenerForSeondaryNavAnchors: MouseEventHandler<HTMLAnchorElement> = (e) => !isCrawler && e.preventDefault();

function SecondaryNavItem({ FundSection, displayClass, cleanURL }: { FundSection: IFundSection; displayClass: string, cleanURL: string }) {
    const { name, id, activeClass } = FundSection;
    const url = `${cleanURL}/${id}/`;

    return (
        <li className={displayClass + " " + activeClass} data-target={id} onClick={handleClick}>
            {addLinksToSecondaryNav ?
                <a href={url} onClick={clickListenerForSeondaryNavAnchors} className="secondary-nav-link">{name}</a> : <>{name}</>}
        </li>
    );
}

function handleClick(event: React.MouseEvent<HTMLElement>) {
    if(isCrawler) return true;
    const currentTarget = event.currentTarget;
    const targetedElementId = currentTarget.dataset.target ?? "";
    jumpToSection(targetedElementId);
}

function useSectionJumpOnURL(FundSections: IFundSection[]) {
    useEffect(() => {
        try {
            const urlPath = window.location.pathname.split("/").reverse();
            const lastPathToken = urlPath[0] == "" ? urlPath[1] : urlPath[0];
            const targetSection = FundSections.find((x) => x.id == lastPathToken);
            const targetSectionId = targetSection?.id;
            if (!targetSectionId) return;
            if (targetSection == FundSections[0]) return;
            window.setTimeout(() => {
                window.scrollY = 0;
                jumpToSection(targetSectionId as string)
            }, 600);
        } catch (error) {
            console.log("useSectionJumpOnURL failed");
            console.log(error);
        }
    }, []);
}

const DesktopDropdownButton = function (props: {
    buttonText: string | null;
    buttonClass: string | null;
    isMobile?: Boolean | false;
}) {
    const { buttonText, buttonClass, isMobile } = props;
    const text = buttonText ?? "";
    const classText = buttonClass ?? "dropdown__button-simple-toggle";
    return (
        <>
            {(text != "" || !isMobile) && (
                <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded="false"
                    aria-label="dropdown"
                    className={`dropdown__button ${classText} dropdown__button-default`}
                    data-toggle="dropdown"
                >
                    {text != "" && <span className="text-util-md dropdown-label">{text}</span>}
                    <svg
                        aria-hidden="true"
                        focusable="false"
                        data-prefix="far"
                        data-icon="chevron-down"
                        className="svg-inline--fa fa-chevron-down fa-w-16 fa-2x dropdown__icon dropdown__icon-default"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 18 18"
                    >
                        <path
                            fill="currentColor"
                            d="M2.26 4.88a.75.75 0 00-.54.21.75.75 0 000 1.07l6.75 6.75a.75.75 0 001.06 0l6.75-6.75a.75.75 0 000-1.07.75.75 0 00-1.06 0L9 11.31 2.78 5.1a.75.75 0 00-.52-.21z"
                        ></path>
                    </svg>
                </button>
            )}
        </>
    );
};